import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { renderMarkdown, getMarkdownStyles } from "../utils/markdownRenderer";

// Optional socket.io realtime — gracefully ignored if not available
let socket = null;
try {
  const { io } = require("socket.io-client");
  const API = process.env.REACT_APP_API_URL || window.location.origin;
  socket = io(API, { transports: ["websocket", "polling"], autoConnect: false });
} catch { /* socket.io-client not installed — realtime disabled */ }

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("rw-RW", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return d; }
};

// Structured reference number — the raw DB id stays internal, this is the
// number that appears on the document: Nº 007/UMU-<VILLAGE>/<MM>/<YYYY>
const refNumber = (r) => {
  const idPart = String(r.raporo_id || r.id || 0).padStart(3, "0");
  const village = (r.umudugudu || "UMUDUGUDU").toUpperCase().replace(/\s+/g, "");
  const d = new Date(r.itariki_ya_raporo || r.created_at || Date.now());
  const mm = String((isNaN(d) ? new Date() : d).getMonth() + 1).padStart(2, "0");
  const yyyy = (isNaN(d) ? new Date() : d).getFullYear();
  return `Nº ${idPart}/UMU-${village}/${mm}/${yyyy}`;
};

const placeDate = (r) => {
  const d = fmtDate(r.itariki_ya_raporo || r.created_at);
  return r.umudugudu ? `I ${r.umudugudu}, kuwa ${d}` : d;
};

const addresseeLabel = (r) => {
  const v = r.uwo_raporo_igenewe;
  if (!v) return "Umuyobozi w'Umudugudu";
  if (v === "Umudugudu" || v === "Akagari") return `Umuyobozi w'${v}`;
  return v;
};

const STATUS_MAP = {
  "Inyandiko itaruzura": { color: "#4A5158", label: "Inyandiko itaruzura" },
  "Itegereje Umudugudu": { color: "#B45309", label: "Itegereje Umudugudu" },
  "Itegereje Akagari":   { color: "#1D4ED8", label: "Itegereje Akagari" },
  "Yemejwe Burundu":     { color: "#1E9E4A", label: "Yemejwe Burundu" },
  "Yasubijwe":           { color: "#DC2626", label: "Yasubijwe" },
};

// Workflow status is screen metadata, not part of the document — text label
// with a state dot, never an emoji, and always excluded from print.
const StatusPill = ({ status }) => {
  const s = STATUS_MAP[status] || { color: "#4A5158", label: status || "Itegereje" };
  return (
    <span
      className="no-print rp-status-pill"
      role="status"
      aria-label={`Uko raporo ihagaze: ${s.label}`}
    >
      <span className="rp-status-dot" style={{ background: s.color }} aria-hidden="true" />
      {s.label}
    </span>
  );
};

const ViewReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [raporo, setRaporo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [forceA4, setForceA4] = useState(false);
  // optimistic local state — set immediately after signing, no need to wait for fetchReport
  const [localSigned, setLocalSigned] = useState(false);
  const [localSignDate, setLocalSignDate] = useState(null);
  const [localSignImg, setLocalSignImg] = useState(null);
  const [localSignerName, setLocalSignerName] = useState(null);
  const [localSignerRole, setLocalSignerRole] = useState(null);
  const pageRef = useRef(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Banza winjire muri konti yawe.");
      setLoading(false);
      setTimeout(() => navigate("/mudugudu"), 1500);
      return;
    }
    try {
      const res = await axiosInstance.get(`/raporo/${id}`).catch(() => axiosInstance.get(`/raporo/${id}/view`));
      setRaporo(res.data);
    } catch {
      setError("Ntibyashobotse kubona raporo. Gerageza nanone.");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Refetch when the tab regains focus, instead of a manual refresh button
  useEffect(() => {
    const onFocus = () => fetchReport();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchReport]);

  // Realtime: listen for receiver_signed event
  useEffect(() => {
    if (!socket) return;
    socket.connect();
    const handler = (data) => {
      if (String(data.raporo_id) === String(id)) {
        toast.info(data.message || "Umudugudu yamaze gusinya raporo");
        fetchReport();
      }
    };
    socket.on("receiver_signed", handler);
    return () => { socket.off("receiver_signed", handler); socket.disconnect(); };
  }, [id, fetchReport]);

  // Render markdown content
  const renderedContent = useMemo(() => {
    return renderMarkdown(raporo?.ibisobanuro || raporo?.report_content || "");
  }, [raporo]);

  const senderSignatureImg = raporo?.signature_image_snapshot || raporo?.sig_image || null;
  const senderSigned = !!(raporo?.signed_at || senderSignatureImg);
  const senderName = raporo?.sig_fullname || raporo?.signed_by_name || raporo?.yakozwe_na || "—";
  const senderRole = raporo?.sig_title || raporo?.signed_by_role || "Umuyobozi";

  const receiverSignatureImg = raporo?.receiver_signature_image || localSignImg || null;
  const isReceiverSigned = !!(raporo?.receiver_signed_at || receiverSignatureImg || localSigned);
  const receiverName = raporo?.receiver_signed_by_name || localSignerName || "—";
  const receiverRole = raporo?.receiver_signed_by_role || localSignerRole || raporo?.uwo_raporo_igenewe || "—";

  const isForwarded = !!(raporo?.statut?.includes("Akagari") || raporo?.forwarded_at);
  const forwardedAt = raporo?.forwarded_at ? fmtDate(raporo.forwarded_at) : null;

  const downloadPdf = async () => {
    if (!pageRef.current || exporting) return;
    setExporting(true);
    const wasForced = forceA4;
    if (!wasForced) setForceA4(true);
    // let the forced-width layout reflow before capturing
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const canvas = await html2canvas(pageRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        ignoreElements: (el) => !!el.classList?.contains("no-print"),
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const idPart = String(raporo?.raporo_id || id).padStart(3, "0");
      pdf.save(`raporo-${idPart}.pdf`);
    } catch {
      toast.error("Ntibyashobotse gukora PDF. Gerageza nanone.");
    } finally {
      if (!wasForced) setForceA4(false);
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div className="spinner-border" style={{ width: "3rem", height: "3rem", color: "#00A1DE" }} />
        <p style={{ marginTop: "1rem", color: "#4A5158", fontFamily: "system-ui, sans-serif" }}>
          Raporo irimo gutegurwa...
        </p>
      </div>
    );
  }

  if (error || !raporo) {
    return (
      <div style={styles.loadingWrap}>
        <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "3rem", color: "#dc2626" }}></i>
        <p style={{ marginTop: "1rem", color: "#dc2626", fontWeight: 600, fontFamily: "system-ui, sans-serif" }}>
          {error || "Raporo ntiboneka, cyangwa ntiwemerewe kuyireba."}
        </p>
        <button onClick={() => navigate(-1)} style={styles.btnSecondary}>
          <i className="bi bi-arrow-left"></i> Subira Inyuma
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600;8..60,700&display=swap');

        @page { size: A4; margin: 18mm 16mm 16mm; }

        @media print {
          .no-print { display: none !important; }
          body { background: #ffffff !important; }
          .rp-a4-scroll { overflow: visible !important; }
          .report-page {
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
            width: auto !important;
            min-height: auto !important;
            padding: 0 !important;
          }
          .rp-sig-grid, .rp-sig-box, .rp-refline, .rp-addressee, table {
            break-inside: avoid;
          }
          .rp-letterhead, .rp-subject, .rp-section-label {
            break-after: avoid;
          }
        }

        button:focus-visible, a:focus-visible {
          outline: 2px solid #00A1DE;
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }

        body {
          background: #D8DBDF;
          margin: 0;
          padding: 0;
        }

        .rp-a4-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .report-page {
          width: 210mm;
          min-height: 297mm;
          background: #FFFFFF;
          margin: 2rem auto;
          padding: 25mm 22mm;
          box-shadow: 0 4px 6px rgba(20,23,26,0.06), 0 10px 40px rgba(20,23,26,0.10);
          font-family: 'Source Serif 4', Cambria, 'Times New Roman', Georgia, serif;
          color: #14171A;
        }

        @media (max-width: 900px) {
          .report-page:not(.report-page--a4-forced) {
            width: 100%;
            min-height: auto;
            margin: 0;
            padding: 2rem 1.25rem 3rem;
            box-shadow: none;
          }
        }

        .report-page--a4-forced {
          margin: 1rem auto;
        }

        .rp-letterhead {
          text-align: center;
          margin-bottom: 0;
        }

        .rp-lh-country {
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #14171A;
        }

        .rp-lh-region {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 1.2px;
          color: #4A5158;
          margin-top: 3px;
        }

        .rp-lh-office {
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #14171A;
          margin-top: 6px;
        }

        .rp-band {
          height: 3px;
          margin: 0.9rem 0 1.75rem;
          background: linear-gradient(to right, #00A1DE 33.33%, #FAD201 33.33%, #FAD201 66.66%, #1E9E4A 66.66%);
        }

        .rp-refline {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          font-size: 0.82rem;
          color: #4A5158;
          margin-bottom: 1.5rem;
        }

        .rp-addressee {
          margin-bottom: 1.25rem;
          font-size: 0.95rem;
        }

        .rp-addressee-label {
          font-weight: 700;
          margin-right: 0.35rem;
        }

        .rp-subject {
          margin-bottom: 1.25rem;
          font-size: 0.95rem;
        }

        .rp-subject-label {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-right: 0.35rem;
        }

        .rp-salutation {
          margin: 0 0 1.25rem;
          font-size: 0.95rem;
        }

        .rp-section-label {
          font-size: 0.78rem;
          font-weight: 700;
          color: #4A5158;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          margin-bottom: 0.85rem;
        }

        .rp-content {
          font-size: 1rem;
          line-height: 1.85;
          color: #14171A;
          text-align: justify;
          hyphens: auto;
          -webkit-hyphens: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .rp-content-empty {
          font-style: italic;
          color: #4A5158;
        }

        .rp-content-rendered {
          white-space: normal;
        }

        .rp-content-rendered table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.92rem;
        }

        .rp-content-rendered th, .rp-content-rendered td {
          border: 1px solid #C9CDD2;
          padding: 0.45rem 0.65rem;
          text-align: left;
        }

        .rp-content-rendered th {
          background: #F2F4F5;
          font-weight: 700;
        }

        .rp-content-rendered td.num, .rp-content-rendered th.num {
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        .rp-closing {
          font-size: 0.9rem;
          color: #4A5158;
          font-style: italic;
          margin: 2rem 0 0;
        }

        .rp-divider {
          border: none;
          border-top: 1px solid #C9CDD2;
          margin: 1.75rem 0;
        }

        .rp-signature-block {
          margin-top: 3rem;
          padding-top: 1.5rem;
          border-top: 1px solid #C9CDD2;
        }

        .rp-sig-official-header {
          font-size: 0.78rem;
          font-weight: 700;
          color: #4A5158;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          margin-bottom: 1.1rem;
        }

        .rp-sig-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        @media (max-width: 700px) {
          .rp-sig-grid { grid-template-columns: 1fr; gap: 1.5rem; }
        }

        .rp-sig-box {
          display: flex;
          flex-direction: column;
        }

        .rp-sig-role-label {
          font-size: 0.78rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #14171A;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 0.75rem;
        }

        .rp-sig-line-area {
          min-height: 64px;
          display: flex;
          align-items: flex-end;
        }

        .rp-sig-blank {
          height: 1px;
          width: 100%;
        }

        .rp-signature-img {
          max-width: 180px;
          max-height: 64px;
          object-fit: contain;
        }

        .rp-sig-rule {
          border: none;
          border-top: 1px solid #14171A;
          margin: 0.4rem 0 0.6rem;
        }

        .rp-sig-name {
          font-size: 0.98rem;
          font-weight: 700;
          color: #14171A;
        }

        .rp-sig-meta {
          font-size: 0.85rem;
          color: #4A5158;
          margin-top: 2px;
        }

        .rp-kashe-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 1rem;
        }

        .rp-kashe-circle {
          flex-shrink: 0;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 1.5px dashed #C9CDD2;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          color: #4A5158;
        }

        .rp-kashe-caption {
          font-size: 0.75rem;
          color: #4A5158;
          font-style: italic;
        }

        .rp-forwarded-note {
          font-size: 0.82rem;
          color: #4A5158;
          margin-top: 0.75rem;
        }

        .rp-footer {
          margin-top: 3.5rem;
          padding-top: 1rem;
          border-top: 1px solid #C9CDD2;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.72rem;
          color: #4A5158;
        }

        .rp-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.08);
          color: #fff;
          font-size: 0.78rem;
          font-weight: 600;
          font-family: system-ui, sans-serif;
        }

        .rp-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .rp-a4-toggle {
          display: none;
          background: none;
          border: 1px solid rgba(255,255,255,0.25);
          color: rgba(255,255,255,0.85);
          border-radius: 20px;
          padding: 5px 12px;
          font-size: 0.75rem;
          font-family: system-ui, sans-serif;
          cursor: pointer;
        }

        @media (max-width: 900px) {
          .rp-a4-toggle { display: inline-flex; align-items: center; gap: 5px; }
        }

        ${getMarkdownStyles()}
      `}</style>

      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} theme="colored" />

      {/* Top action bar */}
      <div className="no-print" style={styles.actionBar}>
        <div style={styles.actionBarInner}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
            <button onClick={() => navigate(-1)} style={styles.btnSecondary}>
              <i className="bi bi-arrow-left"></i> Subira Inyuma
            </button>
            <StatusPill status={raporo.statut} />
            <button
              className="rp-a4-toggle"
              onClick={() => setForceA4((v) => !v)}
              aria-pressed={forceA4}
            >
              <i className="bi bi-file-earmark-text"></i>
              {forceA4 ? "Subira ku ipaji" : "Reba nk'uko izacapwa"}
            </button>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={() => window.print()} style={styles.btnSecondary}>
              <i className="bi bi-printer-fill"></i> Kura (Print)
            </button>
            <button onClick={downloadPdf} disabled={exporting} style={styles.btnPrimary}>
              {exporting
                ? <><span className="spinner-border spinner-border-sm" style={{ width: "0.9rem", height: "0.9rem" }}></span> Biratunganywa...</>
                : <><i className="bi bi-file-earmark-arrow-down-fill"></i> Kuramo PDF</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* A4 Page */}
      <div className="rp-a4-scroll">
        <div className={`report-page${forceA4 ? " report-page--a4-forced" : ""}`} ref={pageRef} lang="rw">
          {/* Letterhead */}
          <div className="rp-letterhead">
            <div className="rp-lh-country">Repubulika y'u Rwanda</div>
            {(raporo.umurenge || raporo.akarere) && (
              <div className="rp-lh-region">
                {[raporo.umurenge ? `Umurenge wa ${raporo.umurenge}` : null, raporo.akarere ? `Akarere ka ${raporo.akarere}` : null]
                  .filter(Boolean).join(" — ")}
              </div>
            )}
            <div className="rp-lh-office">
              Ubuyobozi bw'Umudugudu{raporo.umudugudu ? ` wa ${raporo.umudugudu}` : ""}
            </div>
          </div>
          <div className="rp-band" aria-hidden="true"></div>

          {/* Reference + place/date */}
          <div className="rp-refline">
            <span>{refNumber(raporo)}</span>
            <span>{placeDate(raporo)}</span>
          </div>

          {/* Addressee */}
          <div className="rp-addressee">
            <div><span className="rp-addressee-label">Kuri:</span>{addresseeLabel(raporo)}</div>
            <div>
              <span className="rp-addressee-label">Bitumye na:</span>
              {raporo.yakozwe_na || "—"}
              {raporo.umudugudu ? `, Umudugudu wa ${raporo.umudugudu}` : ""}
            </div>
          </div>

          {/* Subject */}
          <div className="rp-subject">
            <span className="rp-subject-label">Impamvu:</span>
            <strong>{raporo.umutwe_wa_raporo || raporo.report_title || "Raporo"}</strong>
          </div>

          <p className="rp-salutation">Nyakubahwa,</p>

          {/* Content */}
          <div className="rp-section-label">Inyandiko ya Raporo</div>
          {renderedContent && renderedContent.trim() ? (
            <div
              className="rp-content rp-content-rendered"
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
          ) : (
            <p className="rp-content rp-content-empty">Nta nyandiko yashyizweho.</p>
          )}

          <p className="rp-closing">
            Iyi raporo yakozwe kandi ishyikirizwa nk'uko bikwiye; ibirimo ni ukuri kandi ni ibyemejweho n'uwayanditse.
          </p>

          {/* Dual Signature Section */}
          <div className="rp-signature-block">
            <div className="rp-sig-official-header">Icyiciro cy'Imikono</div>

            <div className="rp-sig-grid">
              {/* Sender */}
              <div className="rp-sig-box">
                <div className="rp-sig-role-label">1. Uwatanze Raporo</div>
                <div className="rp-sig-line-area">
                  {senderSignatureImg
                    ? <img src={senderSignatureImg} alt="Umukono w'uwatanze raporo" className="rp-signature-img" />
                    : <div className="rp-sig-blank" />
                  }
                </div>
                <hr className="rp-sig-rule" />
                {senderSigned ? (
                  <>
                    <div className="rp-sig-name">{senderName}</div>
                    <div className="rp-sig-meta">{senderRole}</div>
                    <div className="rp-sig-meta">
                      {raporo.signed_at ? `Yasinye kuwa ${fmtDate(raporo.signed_at)}` : ""}
                    </div>
                  </>
                ) : (
                  <div className="rp-sig-meta">Ntiyarasinya</div>
                )}
                <div className="rp-kashe-row">
                  <div className="rp-kashe-circle" aria-hidden="true">KASHE</div>
                  <span className="rp-kashe-caption">Umukono na Kashe</span>
                </div>
              </div>

              {/* Receiver */}
              <div className="rp-sig-box">
                <div className="rp-sig-role-label">
                  2. Uwakiriye Raporo
                  {!isReceiverSigned && (
                    <button
                      className="no-print"
                      onClick={() => navigate(`/sign-receiver/${id}`)}
                      title="Shyira Umukono w'Uwakiriye"
                      style={{
                        marginLeft: "auto", width: 30, height: 30, borderRadius: "50%",
                        border: "1px solid #C9CDD2", cursor: "pointer",
                        background: "#F2F4F5", color: "#14171A",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-pen-fill" style={{ fontSize: "0.78rem" }}></i>
                    </button>
                  )}
                </div>
                <div className="rp-sig-line-area">
                  {receiverSignatureImg
                    ? <img src={receiverSignatureImg} alt="Umukono w'uwakiriye raporo" className="rp-signature-img" />
                    : <div className="rp-sig-blank" />
                  }
                </div>
                <hr className="rp-sig-rule" />
                {isReceiverSigned ? (
                  <>
                    <div className="rp-sig-name">{receiverName}</div>
                    <div className="rp-sig-meta">{receiverRole}</div>
                    <div className="rp-sig-meta">
                      {`Yasinye kuwa ${raporo.receiver_signed_at ? fmtDate(raporo.receiver_signed_at) : (localSignDate || "—")}`}
                    </div>
                  </>
                ) : (
                  <div className="rp-sig-meta">Ntiyarasinya</div>
                )}
                <div className="rp-kashe-row">
                  <div className="rp-kashe-circle" aria-hidden="true">KASHE</div>
                  <span className="rp-kashe-caption">Umukono na Kashe</span>
                </div>
                {isForwarded && (
                  <div className="rp-forwarded-note">
                    Yoherejwe ku Kagari{forwardedAt ? ` kuwa ${forwardedAt}` : ""}.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="rp-footer">
            <span>Sisitemu y'Umudugudu</span>
            <span>Ivuye kuri sisitemu kuwa {fmtDate(new Date())}</span>
          </div>
        </div>
      </div>

      {/* Bottom spacing */}
      <div className="no-print" style={{ height: "3rem" }}></div>
    </>
  );
};

const styles = {
  loadingWrap: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#D8DBDF",
    gap: "0.5rem",
  },
  actionBar: {
    background: "#1e293b",
    padding: "0.75rem 1.5rem",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  actionBarInner: {
    maxWidth: "210mm",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    flexWrap: "wrap",
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.6rem 1.25rem",
    background: "#00A1DE",
    border: "none",
    borderRadius: 10,
    color: "white",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    fontFamily: "system-ui, sans-serif",
  },
  btnSecondary: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.6rem 1.25rem",
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 10,
    color: "white",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    fontFamily: "system-ui, sans-serif",
  },
};

export default ViewReport;
